import React, { useRef, useCallback } from 'react';
import { InformeResponse } from '@/types';
import { Card } from './Card';
import { getNivelLabel, getPosicionLabel, getCategoriaLabel, getCompeticionLabel } from '@/types/enums';

interface ListaInformesProps {
  informes: InformeResponse[];
  loading: boolean;
  onSeleccionarInforme: (informe: InformeResponse) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

  /**
   * Formatea la fecha para mostrar
   */
  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

/**
 * Componente para mostrar la lista de informes
 * @param informes - Lista de informes a mostrar
 * @param loading - Estado de carga
 * @param onSeleccionarInforme - Función para seleccionar un informe
 * @param onLoadMore - Función para cargar más informes (infinite scroll)
 * @param hasMore - Indica si hay más informes para cargar
 */
export const ListaInformes: React.FC<ListaInformesProps> = ({
  informes,
  loading,
  onSeleccionarInforme,
  onLoadMore,
  hasMore = false
}) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback((node: HTMLDivElement | null) => {
    if (loading) return;
    
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && onLoadMore) {
        onLoadMore();
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore, onLoadMore]);

  // Si está cargando y no hay informes (carga inicial), mostrar spinner centrado
  if (loading && informes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <span className="ml-2 text-secondary-300">Cargando informes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-secondary-50">
          Tus Informes
        </h2>
      </div>

      {/* Lista de informes */}
      {(!informes || (informes.length === 0 && !loading)) ? (
        <Card className="p-6 text-center">
          <p className="text-secondary-400">
            No hay informes disponibles
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {informes && informes.map((informe, index) => {
             // Solo poner la referencia al último elemento si es el último
             const isLastElement = index === informes.length - 1;
             
             return (
            <div key={`${informe.id}-${index}`} ref={isLastElement ? lastElementRef : null}>
              <Card
                className="p-4 cursor-pointer hover:bg-secondary-700 transition-colors"
                onClick={() => onSeleccionarInforme(informe)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {/* Nombre completo */}
                    <h3 className="font-semibold text-secondary-50 mb-2">
                      {informe.nombreCompleto}
                    </h3>
                    
                    {/* Información en grid */}
                    <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                      <div>
                        <span className="text-secondary-400">Equipo: </span>
                        <span className="text-secondary-300">
                          {informe.nombreEquipo} {informe.letraEquipo}
                        </span>
                      </div>
                      <div>
                        <span className="text-secondary-400">Posición: </span>
                        <span className="text-secondary-300">
                          {getPosicionLabel(informe.posicion)}
                        </span>
                      </div>
                      <div>
                        <span className="text-secondary-400">Competición: </span>
                        <span className="text-secondary-300">
                          {getCompeticionLabel(informe.competicion || informe.competicionPartido)}
                        </span>
                      </div>
                      <div>
                        <span className="text-secondary-400">Categoría: </span>
                        <span className="text-secondary-300">
                          {getCategoriaLabel(informe.categoria)}
                        </span>
                      </div>
                      <div>
                        <span className="text-secondary-400">Grupo: </span>
                        <span className="text-secondary-300">{informe.grupo}</span>
                      </div>
                      <div>
                        <span className="text-secondary-400">Año: </span>
                        <span className="text-secondary-300">{informe.año}</span>
                      </div>
                    </div>

                    {/* Información adicional */}
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <span className="text-secondary-400">Nivel: </span>
                        <span className={`font-medium ${
                          informe.nivel === 'FICHAR' ? 'text-green-400' :
                          informe.nivel === 'INTERESANTE' ? 'text-blue-400' :
                          informe.nivel === 'SEGUIR' ? 'text-yellow-400' :
                          'text-red-400'
                        }`}>
                          {getNivelLabel(informe.nivel)}
                        </span>
                      </div>
                      <div className="text-secondary-400">
                        Creado: {formatearFecha(informe.createdAt)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Indicador de foto */}
                  {informe.tieneFoto && (
                    <div className="ml-3 flex-shrink-0">
                      <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-primary-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )})}
          
          {/* Elemento sentinela para infinite scroll */}
          {hasMore && (
            <div ref={lastElementRef} className="h-10 flex items-center justify-center">
               {loading && (
                 <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
               )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};